import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ChevronDoubleRightIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  ChatBubbleBottomCenterIcon,
  CalendarIcon,
  UserGroupIcon,
  CogIcon,
  ChartBarIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  EyeIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsRightLeftIcon,
  Square2StackIcon,
  XCircleIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  UserCircleIcon,
  PaperAirplaneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export default function TaskCalendarTeamHelp() {
  const [activePopup, setActivePopup] = useState(null);

  const showPopup = (popupId) => {
    setActivePopup(popupId);
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  const Popup = ({ id, title, description, isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 relative">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">{title}</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <p className="text-gray-700 leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserGroupIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Team Task Calendar</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive team task management interface for managers to oversee,
            coordinate, and optimize team productivity across all scheduled tasks.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Team Task Calendar Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <UserGroupIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Team Oversight</h3>
                <p className="text-gray-600">
                  Monitor all team members' tasks, progress, and performance in real-time.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <CogIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Task Management</h3>
                <p className="text-gray-600">
                  Change, duplicate, delete, and reassign tasks with priority controls.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Analytics & Insights</h3>
                <p className="text-gray-600">
                  Progress tracking, bottleneck analysis, and completion statistics.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleBottomCenterIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Communication</h3>
                <p className="text-gray-600">
                  Send messages, track issues, and coordinate team activities.
                </p>
              </div>
            </div>

            {/* Static Team Calendar Demo */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Team Task Calendar Interface</h3>

              {/* Static Header */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-8">
                <div className="bg-white shadow p-3 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
                    <h1 className="text-lg sm:text-xl font-bold text-indigo-600 truncate">
                      Mon, Oct 14, 2025
                    </h1>
                    <input
                      type="date"
                      value="2025-10-14"
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs sm:text-sm font-medium text-gray-600 shadow-sm"
                      style={{ maxWidth: '140px' }}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 items-center">
                    <select className="rounded-md bg-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-gray-600 shadow-sm">
                      <option>All Roles</option>
                      <option>Manager</option>
                      <option>Staff</option>
                      <option>Cashier</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => showPopup('zoom-out')}
                      className="rounded-md bg-gray-200 p-1 sm:px-3 sm:py-2 text-gray-800 shadow-sm hover:bg-gray-300 cursor-pointer transition-colors"
                    >
                      <MagnifyingGlassMinusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                    <button
                      onClick={() => showPopup('zoom-in')}
                      className="rounded-md bg-gray-200 p-1 sm:px-3 sm:py-2 text-gray-800 shadow-sm hover:bg-gray-300 cursor-pointer transition-colors"
                    >
                      <MagnifyingGlassPlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </div>

                  <div className="flex items-center">
                    <span className="text-xs sm:text-sm text-gray-600">
                      10:30:45
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-white p-2 sm:p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:space-x-4">
                    <button
                      onClick={() => showPopup('bottlenecks')}
                      className="inline-flex items-center gap-x-1 rounded-md bg-indigo-600 px-2 py-1 sm:px-3.5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer transition-colors"
                    >
                      <ChevronDoubleRightIcon className="size-4 sm:size-5" />
                      Bottlenecks
                    </button>

                    <div className="w-full bg-gray-200 rounded-full h-4 sm:h-6 mb-2 sm:mb-4 overflow-hidden relative">
                      <div className="flex h-full">
                        <div className="bg-emerald-500 transition-all duration-300 ease-out" style={{ width: '65%' }} />
                        <div className="bg-red-500 transition-all duration-300 ease-out" style={{ width: '35%' }} />
                      </div>
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-xs font-medium">
                        <span className="text-[10px] sm:text-xs whitespace-nowrap">
                          13 Done • 7 Todo
                          <span className="text-gray-300 mx-1 hidden sm:inline">|</span>
                          <span className="sm:inline">20 Total</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Groups Demo */}
              <div className="space-y-6">
                {/* Time Group 1 */}
                <div className="mb-4 sm:mb-6">
                  <div className="mb-2 bg-white/80 backdrop-blur-sm p-2 rounded-md shadow-sm">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-700">Tasks at 09:00</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                    {/* Task Card 1 - Completed */}
                    <div className="bg-red-500 flex flex-col w-full rounded-md shadow-md text-white relative overflow-hidden p-4">
                      <div className="flex items-center justify-between">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-indigo-600 font-semibold">
                          1
                        </div>
                        <p className="text-xs p-2 bg-indigo-600 inline-block font-bebas rounded-md">Sales</p>
                        <button
                          onClick={() => showPopup('task-details')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded"
                        >
                          <ChevronDoubleRightIcon className="w-10 h-10 md:w-16 md:h-16 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('duplicate-task')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded p-1"
                        >
                          <Square2StackIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('delete-task')}
                          className="cursor-pointer transition-colors hover:bg-red-200 rounded p-1"
                        >
                          <XCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-red-100" />
                        </button>
                      </div>

                      <p className="text-sm font-semibold mt-2">
                        John Smith - Manager
                      </p>
                      <p className="text-xs">Sales Process</p>
                      <div className="flex items-center space-x-2 mt-2 flex-wrap">
                        <button>
                          <CheckCircleIcon className="h-12 w-12 text-emerald-300 animate-bounce" />
                        </button>
                        <span className="text-xs bg-red-500 text-white rounded-full px-2 py-1">
                          High!
                        </span>
                        <button
                          onClick={() => showPopup('decrease-priority')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded px-2 py-1 text-xs font-semibold text-emerald-500"
                        >
                          <MinusCircleIcon className="h-5 w-5 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('increase-priority')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded px-2 py-1 bg-transparent text-xs font-semibold text-emerald-500"
                        >
                          <PlusCircleIcon className="h-5 w-5 text-white" />
                        </button>
                      </div>
                      <p className="font-semibold mt-2">Customer Service Call</p>
                      <p className="text-sm mt-2">Handle incoming customer inquiries</p>
                      <p className="text-sm mt-2">
                        <time>09:00</time> - <time>10:00</time>
                      </p>

                      <div className="flex items-center space-x-2 mt-2">
                        <button
                          onClick={() => showPopup('message-staff')}
                          className="cursor-pointer transition-colors hover:bg-indigo-700 rounded-md mr-2 sm:mr-3 bg-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-indigo-600"
                        >
                          <ChatBubbleBottomCenterIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => showPopup('reassign-task')}
                          className="cursor-pointer transition-colors hover:bg-emerald-700 rounded-md bg-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-emerald-500"
                        >
                          <ArrowsRightLeftIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    {/* Task Card 2 - Current */}
                    <div className="bg-emerald-500 flex flex-col w-full rounded-md shadow-md text-white relative overflow-hidden p-4">
                      <div className="flex items-center justify-between">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-indigo-600 font-semibold">
                          1
                        </div>
                        <p className="text-xs p-2 bg-indigo-600 inline-block font-bebas rounded-md">Inventory</p>
                        <button
                          onClick={() => showPopup('task-details')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded"
                        >
                          <ChevronDoubleRightIcon className="w-10 h-10 md:w-16 md:h-16 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('duplicate-task')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded p-1"
                        >
                          <Square2StackIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('delete-task')}
                          className="cursor-pointer transition-colors hover:bg-red-200 rounded p-1"
                        >
                          <XCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-red-100" />
                        </button>
                      </div>

                      <p className="text-sm font-semibold mt-2">
                        Sarah Johnson - Staff
                      </p>
                      <p className="text-xs">Inventory Management</p>
                      <div className="flex items-center space-x-2 mt-2 flex-wrap">
                        <UserCircleIcon className="h-6 w-6 text-white" />
                        <span className="text-xs bg-yellow-500 text-white rounded-full px-2 py-1">
                          Medium
                        </span>
                        <button className="rounded px-2 py-1 text-xs font-semibold text-emerald-500">
                          <MinusCircleIcon className="h-5 w-5 text-white" />
                        </button>
                        <button className="rounded px-2 py-1 bg-transparent text-xs font-semibold text-emerald-500">
                          <PlusCircleIcon className="h-5 w-5 text-white" />
                        </button>
                      </div>
                      <p className="font-semibold mt-2">Stock Count</p>
                      <p className="text-sm mt-2">Perform inventory count and update stock levels</p>
                      <p className="text-sm mt-2">
                        <time>09:00</time> - <time>10:00</time>
                      </p>

                      <div className="flex items-center space-x-2 mt-2">
                        <button className="rounded-md mr-2 sm:mr-3 bg-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-indigo-600">
                          <ChatBubbleBottomCenterIcon className="h-3 w-3" />
                        </button>
                        <button className="rounded-md bg-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-emerald-500">
                          <ArrowsRightLeftIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    {/* Task Card 3 - Upcoming */}
                    <div className="bg-indigo-600 flex flex-col w-full rounded-md shadow-md text-white relative overflow-hidden p-4">
                      <div className="flex items-center justify-between">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-indigo-600 font-semibold">
                          2
                        </div>
                        <p className="text-xs p-2 bg-indigo-600 inline-block font-bebas rounded-md">Cash Handling</p>
                        <button>
                          <ChevronDoubleRightIcon className="w-10 h-10 md:w-16 md:h-16 text-white" />
                        </button>
                        <button>
                          <Square2StackIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                        </button>
                        <button>
                          <XCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-red-100" />
                        </button>
                      </div>

                      <p className="text-sm font-semibold mt-2">
                        Mike Davis - Cashier
                      </p>
                      <p className="text-xs">Cash Handling Process</p>
                      <div className="flex items-center space-x-2 mt-2 flex-wrap">
                        <ClockIcon className="h-6 w-6 text-white" />
                        <span className="text-xs bg-green-500 text-white rounded-full px-2 py-1">
                          Low
                        </span>
                        <button
                          onClick={() => showPopup('decrease-priority')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded px-2 py-1 text-xs font-semibold text-emerald-500"
                        >
                          <MinusCircleIcon className="h-5 w-5 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('increase-priority')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded px-2 py-1 bg-transparent text-xs font-semibold text-emerald-500"
                        >
                          <PlusCircleIcon className="h-5 w-5 text-white" />
                        </button>
                      </div>
                      <p className="font-semibold mt-2">Cash Register Balancing</p>
                      <p className="text-sm mt-2">Balance cash register and prepare deposit</p>
                      <p className="text-sm mt-2">
                        <time>09:00</time> - <time>10:00</time>
                      </p>

                      <div className="flex items-center space-x-2 mt-2">
                        <button
                          onClick={() => showPopup('message-staff')}
                          className="cursor-pointer transition-colors hover:bg-indigo-700 rounded-md mr-2 sm:mr-3 bg-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-indigo-600"
                        >
                          <ChatBubbleBottomCenterIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => showPopup('reassign-task')}
                          className="cursor-pointer transition-colors hover:bg-emerald-700 rounded-md bg-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-emerald-500"
                        >
                          <ArrowsRightLeftIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time Group 2 */}
                <div className="mb-4 sm:mb-6">
                  <div className="mb-2 bg-white/80 backdrop-blur-sm p-2 rounded-md shadow-sm">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-700">Tasks at 11:00</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                    {/* Break Task */}
                    <div className="flex flex-col w-full rounded-md shadow-md text-white relative overflow-hidden p-4"
                         style={{background: 'linear-gradient(to bottom, #a78bfa 0%, #a78bfa 50%, #a78bfa 50%, #6d28d9 100%)'}}>
                      <div className="absolute top-2 left-2 text-xs font-semibold text-gray-800 bg-purple-200 px-2 py-1 rounded-md z-10">
                        11:00 - 11:15 ( Break )
                      </div>
                      <div className="flex items-center justify-between mt-8">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-indigo-600 font-semibold">
                          B
                        </div>
                        <button
                          onClick={() => showPopup('task-details')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded"
                        >
                          <ChevronDoubleRightIcon className="w-10 h-10 md:w-16 md:h-16 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('duplicate-task')}
                          className="cursor-pointer transition-colors hover:bg-white/20 rounded p-1"
                        >
                          <Square2StackIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                        </button>
                        <button
                          onClick={() => showPopup('delete-task')}
                          className="cursor-pointer transition-colors hover:bg-red-200 rounded p-1"
                        >
                          <XCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-red-100" />
                        </button>
                      </div>

                      <p className="text-sm font-semibold mt-2">
                        Team Break
                      </p>
                      <p className="text-xs">Scheduled Break</p>
                      <div className="flex items-center space-x-2 mt-2 flex-wrap">
                        <ClockIcon className="h-6 w-6 text-white" />
                      </div>
                      <p className="font-semibold mt-2">Break Time</p>
                      <p className="text-sm mt-2">Team break and rest period</p>
                      <p className="text-sm mt-2">
                        <time>11:00</time> - <time>11:15</time>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interface Explanations */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <h4 className="text-lg font-bold text-blue-900">Date Navigation</h4>
                  </div>
                  <p className="text-blue-700 text-sm leading-relaxed">
                    Select specific dates or navigate between days to view team schedules.
                    Shows current date and provides calendar picker for easy date selection.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <h4 className="text-lg font-bold text-indigo-900">Role Filtering</h4>
                  </div>
                  <p className="text-indigo-700 text-sm leading-relaxed">
                    Filter tasks by staff roles (Manager, Staff, Cashier, etc.) to focus
                    on specific team segments or view all roles simultaneously.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <h4 className="text-lg font-bold text-emerald-900">Zoom Controls</h4>
                  </div>
                  <p className="text-emerald-700 text-sm leading-relaxed">
                    Magnify or reduce the task list view for better visibility.
                    Useful when managing large teams or detailed task information.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">4</span>
                    </div>
                    <h4 className="text-lg font-bold text-yellow-900">Progress Tracking</h4>
                  </div>
                  <p className="text-yellow-700 text-sm leading-relaxed">
                    Visual progress bar showing completed vs. pending tasks.
                    Displays real-time statistics for team productivity monitoring.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-red-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">5</span>
                    </div>
                    <h4 className="text-lg font-bold text-red-900">Bottleneck Analysis</h4>
                  </div>
                  <p className="text-red-700 text-sm leading-relaxed">
                    Advanced analytics to identify workflow bottlenecks and
                    optimization opportunities across team processes.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">6</span>
                    </div>
                    <h4 className="text-lg font-bold text-purple-900">Time-Grouped Tasks</h4>
                  </div>
                  <p className="text-purple-700 text-sm leading-relaxed">
                    Tasks organized by start time slots for easy scheduling overview.
                    Shows concurrent activities and team coordination needs.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-teal-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">7</span>
                    </div>
                    <h4 className="text-lg font-bold text-teal-900">Task Status Indicators</h4>
                  </div>
                  <p className="text-teal-700 text-sm leading-relaxed">
                    Color-coded task states: Blue (upcoming), Green (current), Red (overdue/completed).
                    Priority badges and completion icons provide quick status assessment.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">8</span>
                    </div>
                    <h4 className="text-lg font-bold text-pink-900">Task Management Tools</h4>
                  </div>
                  <p className="text-pink-700 text-sm leading-relaxed">
                    Duplicate, delete, reassign, and reprioritize tasks. Change task types
                    and manage team assignments with comprehensive editing controls.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-cyan-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">9</span>
                    </div>
                    <h4 className="text-lg font-bold text-cyan-900">Communication Hub</h4>
                  </div>
                  <p className="text-cyan-700 text-sm leading-relaxed">
                    Send messages to team members, track issues, and coordinate activities.
                    Integrated messaging system for task-related communication.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-orange-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">10</span>
                    </div>
                    <h4 className="text-lg font-bold text-orange-900">Task Details Panel</h4>
                  </div>
                  <p className="text-orange-700 text-sm leading-relaxed">
                    Expandable side panel showing detailed substeps, outcomes, and
                    additional task information for comprehensive task management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works - Step by Step */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <ArrowRightIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">How Team Task Calendar Works</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Select Date & Filter Team</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Choose the date you want to view and filter by team roles to focus on specific
                    segments of your workforce.
                  </p>

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-blue-900 mb-3">Date Selection</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Use calendar picker for specific dates</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Navigate with previous/next buttons</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">View current date by default</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Real-time clock display</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h5 className="font-semibold text-blue-900 mb-2">Role Filtering</h5>
                        <div className="text-sm text-blue-700">
                          <div className="bg-blue-50 p-2 rounded mb-2">
                            <strong>All Roles:</strong> View entire team
                          </div>
                          <div className="bg-blue-50 p-2 rounded mb-2">
                            <strong>Manager:</strong> Management tasks only
                          </div>
                          <div className="bg-blue-50 p-2 rounded mb-2">
                            <strong>Staff:</strong> General staff tasks
                          </div>
                          <div className="bg-blue-50 p-2 rounded">
                            <strong>Cashier:</strong> Cash handling tasks
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Monitor Team Progress</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Track overall team performance with progress bars, completion statistics,
                    and real-time status updates.
                  </p>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-indigo-900 mb-3">Progress Tracking</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Visual progress bar with percentages</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Completed vs. pending task counts</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Real-time updates as tasks complete</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Team productivity metrics</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <h5 className="font-semibold text-indigo-900 mb-2">Task States</h5>
                        <div className="space-y-2 text-sm text-indigo-700">
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-indigo-600 rounded-full mr-2"></div>
                            <span>Upcoming tasks</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                            <span>Currently active</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                            <span>Completed/overdue</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-yellow-400 rounded-full mr-2"></div>
                            <span>Break periods</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <h5 className="font-semibold text-indigo-900 mb-2">Priority Levels</h5>
                        <div className="space-y-2 text-sm text-indigo-700">
                          <div className="bg-red-50 p-2 rounded">
                            <strong className="text-red-600">High:</strong> Urgent tasks
                          </div>
                          <div className="bg-yellow-50 p-2 rounded">
                            <strong className="text-yellow-600">Medium:</strong> Important tasks
                          </div>
                          <div className="bg-green-50 p-2 rounded">
                            <strong className="text-green-600">Low:</strong> Routine tasks
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Manage Team Tasks</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Take control of team assignments with comprehensive task management tools
                    for optimization and coordination.
                  </p>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Task Controls</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Change task types and assignments</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Duplicate tasks for multiple team members</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Delete unnecessary or cancelled tasks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Adjust task priorities dynamically</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Priority Management</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Increase priority for urgent tasks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Decrease priority for less critical items</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Visual priority indicators on task cards</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Real-time priority updates</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-pink-500 to-red-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Analyze & Optimize</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Use advanced analytics to identify bottlenecks, optimize workflows,
                    and improve team productivity.
                  </p>

                  <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-pink-900 mb-3">Bottleneck Analysis</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Identify workflow bottlenecks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Process efficiency analysis</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Resource allocation insights</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Performance optimization recommendations</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-pink-900 mb-3">Communication Tools</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Send messages to team members</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Track issue resolution progress</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Coordinate team activities</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">View task outcomes and feedback</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-pink-200">
                        <h5 className="font-semibold text-pink-900 mb-2">Task Details Panel</h5>
                        <div className="text-sm text-pink-700">
                          <div className="bg-pink-50 p-2 rounded mb-2">
                            <strong>Substep Viewer:</strong> Detailed procedures
                          </div>
                          <div className="bg-pink-50 p-2 rounded mb-2">
                            <strong>Message History:</strong> Communication logs
                          </div>
                          <div className="bg-pink-50 p-2 rounded mb-2">
                            <strong>Outcome Tracking:</strong> Completion records
                          </div>
                          <div className="bg-pink-50 p-2 rounded">
                            <strong>Issue Management:</strong> Problem resolution
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-8 py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Technical Features & Capabilities</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <CalendarIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Date Navigation</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Flexible date selection and navigation</li>
                  <li>• Calendar picker integration</li>
                  <li>• Previous/next day controls</li>
                  <li>• Current date highlighting</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <UserGroupIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Role-Based Filtering</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Dynamic role filtering system</li>
                  <li>• Team segment isolation</li>
                  <li>• Comprehensive role management</li>
                  <li>• Filtered view persistence</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                    <ChartBarIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Progress Analytics</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Real-time progress tracking</li>
                  <li>• Completion percentage calculations</li>
                  <li>• Team productivity metrics</li>
                  <li>• Visual progress indicators</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                    <ArrowsRightLeftIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Task Management</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Dynamic task reassignment</li>
                  <li>• Task duplication capabilities</li>
                  <li>• Priority adjustment controls</li>
                  <li>• Task deletion and modification</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-600 rounded-lg flex items-center justify-center mr-3">
                    <EyeIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Zoom Interface</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Responsive zoom controls</li>
                  <li>• Task list scaling</li>
                  <li>• Mobile-optimized viewing</li>
                  <li>• Accessibility features</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
                    <ChatBubbleBottomCenterIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Communication System</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Integrated messaging platform</li>
                  <li>• Task-specific conversations</li>
                  <li>• Real-time message delivery</li>
                  <li>• Communication history tracking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Troubleshooting & Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Common Issues</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">No Tasks Displayed</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Check selected date has scheduled tasks</li>
                        <li>• Verify role filter isn't too restrictive</li>
                        <li>• Refresh page to reload team data</li>
                        <li>• Check team member assignments</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Task Changes Not Saving</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Ensure stable internet connection</li>
                        <li>• Wait for confirmation after changes</li>
                        <li>• Check user permissions for task management</li>
                        <li>• Refresh page if changes don't appear</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Zoom Issues</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Reset zoom to default level</li>
                        <li>• Check browser zoom settings</li>
                        <li>• Clear browser cache</li>
                        <li>• Try different browser or device</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                  <h3 className="text-lg font-bold text-yellow-900 mb-3">Performance Optimization</h3>
                  <ul className="space-y-2 text-sm text-yellow-700">
                    <li>• Use role filters to reduce data load</li>
                    <li>• Limit view to current day when possible</li>
                    <li>• Close unused task detail panels</li>
                    <li>• Use zoom appropriately for your screen size</li>
                    <li>• Regular browser cache clearing</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Best Practices</h3>
                  <ul className="space-y-3 text-sm text-blue-700">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Review team schedules daily to ensure proper coverage</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use role filters to focus on specific team segments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Monitor progress bars for team productivity insights</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Address high-priority tasks promptly</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use bottleneck analysis for workflow optimization</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Communicate regularly with team members through messages</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Reassign tasks when team members need support</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-green-900 mb-3">Team Management Tips</h3>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Balance workloads across team members</li>
                    <li>• Monitor break compliance for all staff</li>
                    <li>• Address issues before they become bottlenecks</li>
                    <li>• Recognize and reward team achievements</li>
                    <li>• Use data-driven insights for staffing decisions</li>
                    <li>• Maintain open communication channels</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Popup Components */}
        {activePopup === 'zoom-in' && (
          <Popup
            title="Zoom In"
            description="Click this button to zoom in and see more detailed time intervals on the calendar."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'zoom-out' && (
          <Popup
            title="Zoom Out"
            description="Click this button to zoom out and see a broader view of the calendar timeline."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'bottlenecks' && (
          <Popup
            title="Bottlenecks Analysis"
            description="This button analyzes your team's workload to identify potential bottlenecks and scheduling conflicts."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'task-details' && (
          <Popup
            title="Task Details"
            description="Click this button to view detailed information about a task, including description, assigned staff, and progress updates."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'duplicate-task' && (
          <Popup
            title="Duplicate Task"
            description="Use this button to create a copy of the selected task, useful for recurring tasks or similar assignments."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'delete-task' && (
          <Popup
            title="Delete Task"
            description="Click this button to permanently remove the selected task from the calendar. This action cannot be undone."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'decrease-priority' && (
          <Popup
            title="Decrease Priority"
            description="Click this button to lower the priority level of a task, changing it from High to Medium or Medium to Low."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'increase-priority' && (
          <Popup
            title="Increase Priority"
            description="Click this button to raise the priority level of a task, changing it from Low to Medium or Medium to High."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'message-staff' && (
          <Popup
            title="Message Staff"
            description="Click this button to send a direct message to the staff member assigned to this task for quick communication."
            isOpen={true}
            onClose={closePopup}
          />
        )}
        {activePopup === 'reassign-task' && (
          <Popup
            title="Reassign Task"
            description="Click this button to reassign the task to a different staff member or change the task assignment."
            isOpen={true}
            onClose={closePopup}
          />
        )}
      </div>
    </div>
  );
}